import { useEffect } from "react";
import { motion } from "framer-motion";
import { breathe, breathStop } from "@/lib/breathSound";

const HOLD = /^\s*(?:and\s+|now\s+)?hold\b/i;
const IN = /\b(breathe in|breath in|inhale|slowly breathe|breathe low|breathe into|breathe that|in through|stand tall|slow breath)\b/i;
const OUT = /\b(release|exhale|let .*(go|fall)|drop|unclench|loosen|soften|relax|shake)\b/i;
const BREATH_WORD = /\b(breathe|breath|hold|release|exhale|inhale)\b/i;

const phaseOf = (line = "") => (HOLD.test(line) ? "hold" : IN.test(line) ? "in" : OUT.test(line) ? "out" : "rest");

const MOTION = {
  in: { animate: { scale: 1.32 }, transition: { duration: 4.5, ease: "easeInOut" } },
  hold: { animate: { scale: 1.32 }, transition: { duration: 0.6 } },
  out: { animate: { scale: 0.88 }, transition: { duration: 5, ease: "easeInOut" } },
  rest: { animate: { scale: [1, 1.07, 1] }, transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } },
};

export default function BreathRing({ line, sound = true, children }) {
  const phase = phaseOf(line);
  useEffect(() => {
    if (sound && line) breathe(BREATH_WORD.test(line) ? phase : "rest");
  }, [line]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => breathStop(), []);
  const m = MOTION[phase];
  return (
    <div className="relative flex items-center justify-center" data-testid="breath-ring" data-phase={phase}>
      <motion.span key={line} aria-hidden animate={m.animate} transition={m.transition}
        className="absolute inset-[-8%] rounded-full border border-primary/40 pointer-events-none"
        style={{ boxShadow: "0 0 40px 6px hsl(var(--primary) / 0.25), inset 0 0 30px hsl(var(--primary) / 0.15)" }} />
      {children}
      <BreathGuide phase={BREATH_WORD.test(line || "") ? phase : null} />
    </div>
  );
}

// "In · Hold · Out" under the ring, the current step lit, in time with her voice.
function BreathGuide({ phase }) {
  const steps = [["in", "In"], ["hold", "Hold"], ["out", "Out"]];
  return (
    <div className={`absolute -bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] transition-opacity duration-700 ${phase && phase !== "rest" ? "opacity-100" : "opacity-0"}`} data-testid="breath-guide">
      {steps.map(([k, l], i) => (
        <span key={k} className="flex items-center gap-3">
          {i > 0 && <span className="text-foreground/25">·</span>}
          <span className={`transition-colors duration-500 ${phase === k ? "text-primary" : "text-foreground/35"}`} data-testid={`breath-guide-${k}`} data-active={phase === k}>{l}</span>
        </span>
      ))}
    </div>
  );
}
